'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';
import type {
  QBReportResponse,
  QBBalanceSheetReport,
  QBARAgingDetailReport,
  QBAPAgingDetailReport,
  QBProfitLossReport,
  IntuitReportResponse,
  IntuitReportRow,
} from '@/lib/quickbooks/types';

interface ReportState<T> {
  data: QBReportResponse<T> | null;
  loading: boolean;
  error: string | null;
}

interface Props {
  balanceSheet: ReportState<QBBalanceSheetReport>;
  arAging:      ReportState<QBARAgingDetailReport>;
  apAging:      ReportState<QBAPAgingDetailReport>;
  profitLoss:   ReportState<QBProfitLossReport>;
}

// ── Parse helpers ──────────────────────────────────────────────────────────

function findRowValueByLabel(report: IntuitReportResponse | undefined, label: string): number | null {
  if (!report?.Rows?.Row) return null;
  const walk = (rows: IntuitReportRow[]): number | null => {
    for (const row of rows) {
      const sumLabel = row.Summary?.ColData?.[0]?.value;
      const sumVal   = row.Summary?.ColData?.[1]?.value;
      if (sumLabel === label && sumVal !== undefined) return parseFloat(sumVal);
      const dataLabel = row.ColData?.[0]?.value;
      const dataVal   = row.ColData?.[1]?.value;
      if (dataLabel === label && dataVal !== undefined) return parseFloat(dataVal);
      if (row.Rows?.Row) {
        const r = walk(row.Rows.Row);
        if (r !== null) return r;
      }
    }
    return null;
  };
  return walk(report.Rows.Row);
}

function sumLastColumnDataRows(report: IntuitReportResponse | undefined): number {
  if (!report?.Rows?.Row) return 0;
  let sum = 0;
  const walk = (rows: IntuitReportRow[]) => {
    for (const row of rows) {
      if (row.type === 'Data' && row.ColData && row.ColData.length > 0) {
        const last = row.ColData[row.ColData.length - 1]?.value;
        if (last) sum += parseFloat(last) || 0;
      }
      if (row.Rows?.Row) walk(row.Rows.Row);
    }
  };
  walk(report.Rows.Row);
  return sum;
}

function formatCurrency(val: number): string {
  if (Math.abs(val) >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (Math.abs(val) >= 1_000)     return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val.toFixed(0)}`;
}

// Mock sparkline trend data (DEMO DATA pill on each tile's sparkline section).
// Headline numbers come from real QB endpoints; the 6-month trend doesn't —
// QB doesn't return historical trend in a single call, would need 6 separate
// fetches per metric. Out of scope for PR 3.
const SPARKLINES: Record<string, number[]> = {
  cash:       [],
  ar:         [],
  ap:         [],
  netIncome:  [],
};

function Sparkline({ points, color }: { points: number[]; color: string }) {
  const w = 100;
  const h = 22;
  if (points.length < 2) return <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block" />;
  const max = Math.max(...points);
  const min = Math.min(...points);
  const range = max - min || 1;
  const xFor = (i: number) => (i / (points.length - 1)) * w;
  const yFor = (v: number) => h - ((v - min) / range) * h;
  const path = points.map((v, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i)} ${yFor(v)}`).join(' ');
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
      <path d={path} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Tile component ────────────────────────────────────────────────────────

interface TileProps {
  label: string;
  icon: string;
  color: string;
  value: string;
  sub: string;
  trend: 'up' | 'down-good' | 'neutral';
  mock: boolean;
  loading: boolean;
  sparklineKey: keyof typeof SPARKLINES;
}

function KPITile({ label, icon, color, value, sub, trend, mock, loading, sparklineKey }: TileProps) {
  return (
    <div
      className="relative rounded-xl p-5 flex flex-col gap-2"
      style={{
        backgroundColor: '#FFFFFF',
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        border: '1px solid #F0F0F0',
      }}
    >
      {mock && <DemoDataPill />}
      {loading ? (
        <div className="space-y-2 animate-pulse">
          <div className="w-9 h-9 rounded-lg bg-gray-100" />
          <div className="h-7 bg-gray-100 rounded w-3/4 mt-2" />
          <div className="h-3 bg-gray-100 rounded w-full" />
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between pr-16">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
              <Icon name={icon} size={18} style={{ color }} />
            </div>
          </div>
          <div>
            <p className="text-2xl font-bold" style={{ color: '#000150' }}>{value}</p>
            <p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>{label}</p>
            <div
              className="flex items-center gap-1 text-[11px] font-semibold mt-1"
              style={{ color: trend === 'up' ? '#10B981' : trend === 'down-good' ? '#10B981' : '#6B7280' }}
            >
              {trend === 'up' && <Icon name="ArrowTrendingUpIcon" size={12} />}
              {trend === 'down-good' && <Icon name="ArrowTrendingDownIcon" size={12} />}
              <span>{sub}</span>
            </div>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-100">
            <Sparkline points={SPARKLINES[sparklineKey]} color={color} />
          </div>
        </>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────

export default function Row2KPIStrip({ balanceSheet, arAging, apAging, profitLoss }: Props) {
  const cash = findRowValueByLabel(balanceSheet.data?.data, 'Operating Cash') ?? 0;
  const arOutstanding = sumLastColumnDataRows(arAging.data?.data);
  const apDue = sumLastColumnDataRows(apAging.data?.data);
  const netIncome = findRowValueByLabel(profitLoss.data?.data, 'Net Income') ?? 0;

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <KPITile
        label="Cash on Hand"
        icon="BanknotesIcon"
        color="#10B981"
        value={formatCurrency(cash)}
        sub="+4% MoM"
        trend="up"
        mock={!!balanceSheet.data?.mock}
        loading={balanceSheet.loading && !balanceSheet.data}
        sparklineKey="cash"
      />
      <KPITile
        label="AR Outstanding"
        icon="DocumentCurrencyDollarIcon"
        color="#F59E0B"
        value={formatCurrency(arOutstanding)}
        sub="+8% vs last week"
        trend="up"
        mock={!!arAging.data?.mock}
        loading={arAging.loading && !arAging.data}
        sparklineKey="ar"
      />
      <KPITile
        label="AP Due"
        icon="CreditCardIcon"
        color="#EF4444"
        value={formatCurrency(apDue)}
        sub="-2% vs last week"
        trend="down-good"
        mock={!!apAging.data?.mock}
        loading={apAging.loading && !apAging.data}
        sparklineKey="ap"
      />
      <KPITile
        label="MTD Net Income"
        icon="ChartPieIcon"
        color="#07B0E9"
        value={formatCurrency(netIncome)}
        sub="vs plan"
        trend="up"
        mock={!!profitLoss.data?.mock}
        loading={profitLoss.loading && !profitLoss.data}
        sparklineKey="netIncome"
      />
    </div>
  );
}
